import { type BridgeData } from '@databases/database-state/state/bridge-data';
import { captureException } from '@sentry/core';

import { OnViewsStateUpdateCommand } from '@canvas/databases-bridge-api/commands';
import { type NativeEmbedFeatureList } from '@canvas/databases-bridge-api/types';
import { type client, type RovoContentBridgeAPI } from '@canvas/rovo-content-bridge-api';

import { getDatabaseViews } from './handlers/get-database-views';

type SendInitialBridgeCommandsParams = {
  /**
   * The local and external client ids for each bridge client.
   */
  clients: {
    localClientId: ReturnType<typeof client>;
    externalClientId: ReturnType<typeof client>;
  };

  /**
   * A list of features with an enabled/disabled status to determine
   * which bridge commands are enabled.
   */
  featureList: NativeEmbedFeatureList;

  /**
   * A factory method to return the current bridge state used among commands.
   */
  getBridgeData: () => BridgeData;
};

/**
 * When the bridge connection is established, we conditionally send initial commands
 * so the client has data about the current state of the databases.
 */
export function sendInitialBridgeCommands(
  bridge: RovoContentBridgeAPI,
  {
    featureList,
    getBridgeData,
    clients: { localClientId, externalClientId },
  }: SendInitialBridgeCommandsParams
) {
  try {
    if (featureList.toolbarActionBar) {
      /**
       * Conditionally send the list of views to the external client for rendering the element toolbar.
       */
      const { contentId } = getBridgeData();
      const views = getDatabaseViews(contentId);

      bridge.send(OnViewsStateUpdateCommand, {
        senderId: localClientId,
        receiverId: externalClientId,
        views,
      });
    }
  } catch (e) {
    captureException(e);
  }
}
